export type Skill = 'speaking' | 'writing';

export const SKILLS: readonly Skill[] = ['speaking', 'writing'];

export const SKILL_LABELS: Record<Skill, string> = { speaking: 'Speaking', writing: 'Writing' };

export type Window = 'boy' | 'moy' | 'eoy';

export interface SchemeLevel {
  score: number;
  label: string;
  color: string;
  description: string;
}

export interface GrowthPeriod {
  schoolYear: number;
  label: string;
  later: 'moy' | 'eoy';
  lastYear: boolean;
  open: boolean;
  growthLabel: string;
}

export interface ReturnTesting {
  total: number;
  matched: number;
  noBoy: number;
  noLater: number;
  unscorable: number;
}

export interface GrowthFigures {
  students: number;
  matched: number;
  levels: number[];
  avg: number | null;
  boyAvg: number | null;
  growth: number | null;
  onTrack: number;
  bridgingPlus: number;
  grid: number[][];
  returnTesting: ReturnTesting;
  tested: number;
  words: number | null;
  boyWords: number | null;
}

export interface GrowthRow {
  key: string;
  label: string;
  sub: string;
  students: number;
  teachers: number;
  shared: number;
  drillable: boolean;
  assignments?: number;
  skills: Record<Skill, GrowthFigures>;
}

export interface GrowthClass {
  id: string;
  name: string;
  period: number | null;
  students: number;
}

export type CellState = 'scored' | 'unscorable' | 'submitted' | 'none';

export interface Cell {
  state: CellState;
  band?: number;
  label?: string;
  color?: string;
  value?: number;
}

export interface SkillRow {
  boy: Cell;
  moy: Cell;
  eoy: Cell;
  change: number | null;
  onTrack: boolean;
}

export interface GrowthStudentRow {
  id: string;
  name: string;
  tags: string[];
  also: string[];
  classes: string[];
  speaking: SkillRow;
  writing: SkillRow;
  status: string;
  hasLater: boolean;
}

export type GrowthLevel = 'district' | 'grade' | 'school' | 'teacher';

export type GroupBy = 'grade' | 'band' | 'school';

export interface GrowthPage {
  level: GrowthLevel;
  period: GrowthPeriod | null;
  dataAsOf: string;
  district: string;
  title: string;
  subtitle: string;
  students: number;
  years: number[];
  currentYear: number;
  scheme: SchemeLevel[];
  totals: Record<Skill, GrowthFigures>;
  byGrade: GrowthRow[];
  byBand: GrowthRow[];
  bySchool: GrowthRow[] | null;
  groups: GrowthRow[];
  grade?: string;
  school?: string;
  teacher?: string;
  classes: GrowthClass[];
  studentRows: GrowthStudentRow[];
  summary?: string;
}

export interface SkillTile {
  matched: number;
  onTrack: number;
}

export interface GrowthTile {
  period: GrowthPeriod | null;
  dataAsOf: string;
  headline: SkillTile;
  skills: Partial<Record<Skill, SkillTile>>;
  students: number;
  tested: number;
  opensAt?: string;
  opensWindow?: Window;
  nextStartsAt?: string;
  years?: number[];
}

export interface GrowthHistoryYear {
  schoolYear: number;
  label: string;
  speaking: SkillRow;
  writing: SkillRow;
}

export interface GrowthIndicator {
  name: string;
  now: number;
  was: number | null;
}

export interface GrowthRubric {
  later: 'moy' | 'eoy';
  indicators: GrowthIndicator[];
  scoredBy: string;
  scoredAt: string;
  gradedBy: string;
}

export interface GrowthResponse {
  window: string;
  schoolYear: number;
  kind: Window;
  skill: Skill;
  band: number;
  label: string;
  color?: string;
  durationSec: number | null;
  wordCount: number | null;
  boyDurationSec: number | null;
  boyWordCount: number | null;
  listenUrl?: string;
  text?: string;
}

export interface GrowthStudent {
  id: string;
  name: string;
  grade: string;
  school: string;
  teachers: string[];
  period: GrowthPeriod | null;
  dataAsOf: string;
  planRoute: string;
  scheme: SchemeLevel[];
  history: GrowthHistoryYear[];
  rubric: Partial<Record<Skill, GrowthRubric>>;
  responses: GrowthResponse[];
}
